import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, Crown } from "lucide-react";
import { Reveal } from "./Reveal";
import { FALLBACK_PLANS } from "./siteData";

const API = process.env.REACT_APP_BACKEND_URL;

const durationLabel = (m) => {
  if (m === 1) return "1 Month";
  if (m === 12) return "1 Year";
  return `${m} Months`;
};

export const Memberships = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/packages`)
      .then((res) => {
        const data = (res.data || []).filter((p) => p && p.name);
        setPlans(data.length ? data : FALLBACK_PLANS);
      })
      .catch(() => setPlans(FALLBACK_PLANS))
      .finally(() => setLoading(false));
  }, []);

  const popularIdx = plans.length > 2 ? 1 : 0;

  return (
    <section id="membership" data-testid="memberships-section" className="bg-zinc-950 py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-gold font-semibold uppercase tracking-[0.25em] text-sm">
            Membership Plans
          </span>
          <h2 className="font-bebas text-5xl sm:text-6xl text-white mt-3 leading-none">
            Simple Plans, Big Results
          </h2>
          <p className="text-zinc-400 mt-4 font-body">
            No hidden charges. Pick a plan and start your journey today.
          </p>
        </Reveal>

        {loading ? (
          <p className="text-center text-zinc-500" data-testid="memberships-loading">Loading plans...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.slice(0, 3).map((p, i) => {
              const popular = i === popularIdx;
              return (
                <Reveal key={p.id || p.name} delay={i * 90}>
                  <div
                    data-testid={`plan-card-${i}`}
                    className={`relative rounded-2xl p-8 h-full flex flex-col ${
                      popular
                        ? "bg-gradient-to-b from-zinc-900 to-black border-2 border-gold shadow-[0_0_40px_-10px_rgba(212,175,55,0.4)]"
                        : "bg-zinc-900 border border-white/10"
                    }`}
                  >
                    {popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-black text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full flex items-center gap-1">
                        <Crown size={13} /> Most Popular
                      </span>
                    )}
                    <h3 className="font-bebas text-3xl text-white tracking-wide">{p.name}</h3>
                    <p className="text-zinc-400 text-sm font-body mt-1">{durationLabel(p.duration_months)}</p>

                    <div className="mt-6 flex items-end gap-1">
                      <span className="text-zinc-400 text-xl mb-1.5">₹</span>
                      <span className="font-bebas text-6xl text-white leading-none">{p.price}</span>
                    </div>

                    <ul className="mt-7 space-y-3 flex-1">
                      <li className="flex items-center gap-3 text-zinc-300 text-sm font-body">
                        <Check size={16} className="text-gold" /> Full gym & cardio access
                      </li>
                      <li className="flex items-center gap-3 text-zinc-300 text-sm font-body">
                        <Check size={16} className="text-gold" /> Group classes included
                      </li>
                      <li className="flex items-center gap-3 text-zinc-300 text-sm font-body">
                        <Check size={16} className="text-gold" />
                        {p.pt_sessions > 0 ? `${p.pt_sessions} personal training sessions` : "Trainer guidance"}
                      </li>
                    </ul>

                    <a
                      href="#contact"
                      data-testid={`plan-cta-${i}`}
                      className={`mt-8 text-center font-bold uppercase tracking-wider px-6 py-3.5 rounded-md transition-all hover:scale-[1.03] ${
                        popular
                          ? "bg-gold hover:bg-gold_dark text-black"
                          : "border border-white/20 text-white hover:border-gold hover:text-gold"
                      }`}
                    >
                      Choose Plan
                    </a>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
